// ── Ready for Pickup: list view, card view and the texts panel (7 Oct) ───────
//
// Manny: the list had every column at the same weight, red on nearly every
// row, and no amount per bike. He asked for a more engaging list, a card view
// he can switch to, sorting by age, paid/unpaid and last-name range, and a
// panel of recent texts IN from the customers whose bikes are waiting.
//
// Waiting time is shown in three tiers so colour means something again:
//   this week (green), 1-4 weeks (amber), over a month (red).

import React from 'react';
import PaymentBadge from './PaymentBadge';

export const TIERS = [
  { key: 'stale', label: 'Over a month', test: d => d > 30, color: '#e74c3c', tintL: '#fdecea', tintD: '#2a1414' },
  { key: 'waiting', label: '1–4 weeks', test: d => d > 7 && d <= 30, color: '#e67e22', tintL: '#fdf1e4', tintD: '#2a1f0f' },
  { key: 'fresh', label: 'This week', test: d => d <= 7, color: '#27ae60', tintL: '#e6f5ec', tintD: '#0f2a1a' },
];
export const tierOf = (days) => TIERS.find(t => t.test(Number(days) || 0)) || TIERS[2];

export const RANGES = [
  { key: 'a-d', label: 'A–D', re: /^[a-d]/i }, { key: 'e-h', label: 'E–H', re: /^[e-h]/i },
  { key: 'i-l', label: 'I–L', re: /^[i-l]/i }, { key: 'm-p', label: 'M–P', re: /^[m-p]/i },
  { key: 'q-t', label: 'Q–T', re: /^[q-t]/i }, { key: 'u-z', label: 'U–Z', re: /^[u-z]/i },
];
// The last word of the name Lightspeed gives ("Michelle Cerle" -> "Cerle").
export const lastNameOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/);
  return parts[parts.length - 1] || '';
};
export const rangeOf = (name) => {
  const ln = lastNameOf(name);
  return RANGES.find(r => r.re.test(ln)) || null;
};

export const SORTS = [
  { key: 'oldest', label: 'Waiting longest' },
  { key: 'newest', label: 'Newest' },
  { key: 'unpaid', label: 'Unpaid first' },
  { key: 'name', label: 'Last name' },
];

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
const fmtPhone = (p) => {
  const n = (p || '').replace(/\D/g, '').slice(-10);
  return n.length === 10 ? `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : (p || '');
};
const initials = (name) => {
  const p = String(name || '').trim().split(/\s+/).filter(Boolean);
  return ((p[0] || '?')[0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
};
export const ago = (when) => {
  const ms = Date.now() - new Date(when).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'yesterday' : `${d}d ago`;
};

// Sort, then cut into the groups the chosen sort reads best in.
export function groupTickets(list, sortKey) {
  const byAge = (a, b) => (b.daysFinished || 0) - (a.daysFinished || 0);
  if (sortKey === 'name') {
    const sorted = [...list].sort((a, b) => lastNameOf(a.customer).localeCompare(lastNameOf(b.customer)) || a.customer.localeCompare(b.customer));
    const groups = RANGES.map(r => ({ key: r.key, label: r.label, items: sorted.filter(t => rangeOf(t.customer) === r) }));
    const other = sorted.filter(t => !rangeOf(t.customer));
    if (other.length) groups.push({ key: 'other', label: 'Other', items: other });
    return groups.filter(g => g.items.length);
  }
  if (sortKey === 'unpaid') {
    const sorted = [...list].sort(byAge);
    return [
      { key: 'unpaid', label: 'Unpaid', items: sorted.filter(t => !t.isPaid) },
      { key: 'paid', label: 'Paid', color: '#27ae60', items: sorted.filter(t => t.isPaid) },
    ].filter(g => g.items.length);
  }
  const newest = sortKey === 'newest';
  const sorted = [...list].sort(newest ? (a, b) => -byAge(a, b) : byAge);
  const tiers = newest ? [...TIERS].reverse() : TIERS;
  return tiers.map(t => ({ key: t.key, label: t.label, color: t.color, items: sorted.filter(x => tierOf(x.daysFinished) === t) }))
    .filter(g => g.items.length);
}

// ── Small pieces ─────────────────────────────────────────────────────────────

function Avatar({ name, days, isDark, size = 38 }) {
  const t = tierOf(days);
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: isDark ? t.tintD : t.tintL, color: t.color, fontWeight: 700, fontSize: size * 0.36, letterSpacing: '0.02em',
      boxShadow: `inset 0 0 0 2px ${t.color}55` }}>
      {initials(name)}
    </div>
  );
}

function AgeBar({ days, T, compact = false }) {
  const t = tierOf(days);
  const pct = Math.min(100, Math.max(4, (Number(days) || 0) / 60 * 100));
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span style={{ fontSize: compact ? 15 : 18, fontWeight: 700, color: t.color, fontVariantNumeric: 'tabular-nums' }}>{days}</span>
        <span style={{ fontSize: 12, color: T.textSub }}>{days === 1 ? 'day' : 'days'}</span>
      </div>
      <div title="The bar fills at 60 days" style={{ height: 5, borderRadius: 3, background: T.barTrack, marginTop: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: t.color, borderRadius: 3 }} />
      </div>
    </div>
  );
}

function Due({ t, payment, theme, T, big = false }) {
  if (t.isPaid) {
    return <span style={{ fontSize: big ? 15 : 13, fontWeight: 700, color: '#27ae60' }}><i className="ti ti-circle-check" style={{ marginRight: 4 }} />Paid</span>;
  }
  return (
    <div>
      <div style={{ fontSize: big ? 22 : 15, fontWeight: 700, color: T.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
        {t.owed == null ? '—' : money(t.owed)}
      </div>
      <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
        {t.paidToDate > 0 ? `due · ${money(t.paidToDate)} paid by link` : 'due at pickup'}
      </div>
      {payment && <div style={{ marginTop: 4 }}><PaymentBadge payment={payment} theme={theme} compact /></div>}
    </div>
  );
}

function Reached({ t, contact, T }) {
  const method = contact?.lastAttempt?.method;
  const icon = method === 'email' ? 'ti-mail' : method === 'call' || method === 'phone' ? 'ti-phone' : 'ti-message';
  return (
    <div style={{ fontSize: 12.5, minWidth: 0 }}>
      {contact ? (
        <div style={{ color: T.text, display: 'flex', alignItems: 'center', gap: 5 }}>
          <i className={`ti ${icon}`} style={{ color: '#27ae60', fontSize: 15 }} />
          <span><b>{contact.count}×</b> · {contact.daysSince === 0 ? 'today' : `${contact.daysSince}d ago`}</span>
        </div>
      ) : (
        <div style={{ color: '#e67e22', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
          <i className="ti ti-bell-ringing" style={{ fontSize: 15 }} />Not contacted yet
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 3, color: T.textSub, fontSize: 11.5 }}>
        {t.phone ? <a href={`tel:${t.phone}`} onClick={e => e.stopPropagation()} style={{ color: '#3498db', textDecoration: 'none' }}>{fmtPhone(t.phone)}</a>
          : <span style={{ fontStyle: 'italic' }}>No phone</span>}
        {t.email ? <i className="ti ti-mail" title={t.email} style={{ fontSize: 14 }} /> : null}
      </div>
    </div>
  );
}

// The ticket total moved after the customer was texted a balance. Kept from
// the old list: up is red, down is blue, and clicking opens the wizard rather
// than sending anything.
function BalanceChanged({ t, theme, onOpen }) {
  const bc = t.balanceChanged;
  if (!bc) return null;
  const up = bc.delta > 0;
  const colour = up ? '#e74c3c' : '#3498db';
  const sent = new Date(bc.quotedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return (
    <span onClick={e => { e.stopPropagation(); onOpen(t); }}
      title={`Texted ${money(bc.quoted)} on ${sent}. The ticket has gone ${up ? 'up' : 'down'} ${money(Math.abs(bc.delta))} since, and now ${money(bc.owedNow)} is owed. Click to send an updated text.`}
      style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 4, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
        background: up ? (theme === 'light' ? '#fdecea' : '#2a0f0f') : (theme === 'light' ? '#e8f2fb' : '#0f1f2e'), color: colour }}>
      {up ? '▲' : '▼'} {money(Math.abs(bc.delta))} since text · Resend ›
    </span>
  );
}

function CustomerName({ t, T, theme, onCustomer, anchorRefs, size = 15 }) {
  return (
    <span ref={el => { if (el) anchorRefs.current[t.customerId] = el; }}
      onClick={e => { e.stopPropagation(); onCustomer(t); }}
      title="Customer details"
      style={{ fontSize: size, fontWeight: 700, color: T.text, cursor: 'pointer',
        borderBottom: `1px dashed ${theme === 'dark' ? '#3a4a6a' : '#c8c8c0'}`, paddingBottom: 1 }}>
      {t.customer}
    </span>
  );
}

function GroupHeader({ g, T }) {
  const owed = g.items.reduce((s, t) => s + (t.isPaid ? 0 : Number(t.owed || 0)), 0);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 8px' }}>
      {g.color && <span style={{ width: 10, height: 10, borderRadius: '50%', background: g.color }} />}
      <span style={{ fontSize: 13, fontWeight: 700, color: T.text, letterSpacing: '0.02em' }}>{g.label}</span>
      <span style={{ fontSize: 12.5, color: T.textSub }}>
        {g.items.length} {g.items.length === 1 ? 'bike' : 'bikes'}{owed > 0 ? ` · ${money(owed)} to collect` : ''}
      </span>
      <span style={{ flex: 1, height: 1, background: T.border }} />
    </div>
  );
}

// A text in from this customer in the window the panel covers.
function TextNudge({ msg, T }) {
  if (!msg) return null;
  return (
    <div title={msg.body} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.text, minWidth: 0 }}>
      <i className="ti ti-message-circle-2-filled" style={{ color: '#3498db', fontSize: 15, flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>“{msg.body || (msg.media ? 'Photo' : '')}”</span>
      <span style={{ color: T.textSub, flexShrink: 0 }}>{ago(msg.createdAt)}</span>
    </div>
  );
}

// ── List view ────────────────────────────────────────────────────────────────

const LIST_COLS = '6px 44px minmax(190px, 2.2fr) minmax(110px, 1fr) minmax(130px, 1.2fr) minmax(120px, 1.1fr) minmax(170px, 1.5fr) 28px';

export function PickupList({ groups, T, theme, selectedId, onOpen, onCustomer, anchorRefs, contactSummary, payments, lastText, bikesPer }) {
  const isDark = theme === 'dark';
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: LIST_COLS, gap: 12, padding: '0 14px 0 0', fontSize: 10.5, fontWeight: 700,
        letterSpacing: '0.08em', textTransform: 'uppercase', color: T.tableHead }}>
        <span /><span /><span>Customer &amp; bike</span><span>Ticket</span><span>Waiting</span><span>Due</span><span>Reached</span><span />
      </div>
      {groups.map(g => (
        <div key={g.key}>
          <GroupHeader g={g} T={T} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {g.items.map(t => {
              const tier = tierOf(t.daysFinished);
              const active = selectedId === t.id;
              const msg = lastText[String(t.id)];
              return (
                <div key={t.id} role="button" tabIndex={0} onClick={() => onOpen(t)}
                  onKeyDown={e => { if (e.key === 'Enter') onOpen(t); }}
                  style={{ display: 'grid', gridTemplateColumns: LIST_COLS, gap: 12, alignItems: 'center',
                    background: active ? T.rowActive : T.card, border: `1px solid ${active ? '#3498db' : T.cardBorder}`,
                    borderRadius: 10, padding: '10px 14px 10px 0', cursor: 'pointer', overflow: 'hidden',
                    transition: 'box-shadow 0.15s, transform 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = isDark ? '0 2px 10px rgba(0,0,0,0.35)' : '0 2px 10px rgba(0,0,0,0.07)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
                  <span style={{ alignSelf: 'stretch', background: tier.color, margin: '-10px 0', borderRadius: '10px 0 0 10px' }} />
                  <Avatar name={t.customer} days={t.daysFinished} isDark={isDark} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <CustomerName t={t} T={T} theme={theme} onCustomer={onCustomer} anchorRefs={anchorRefs} />
                      {bikesPer[t.customerId] > 1 && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#3498db', background: isDark ? '#0f1f2e' : '#e8f2fb', borderRadius: 4, padding: '1px 6px' }}>{bikesPer[t.customerId]} bikes</span>}
                    </div>
                    <div style={{ fontSize: 12.5, color: T.textSub, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <i className="ti ti-bike" style={{ marginRight: 5 }} />{t.bike}
                    </div>
                    {msg && <div style={{ marginTop: 4 }}><TextNudge msg={msg} T={T} /></div>}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: T.textSub }}>{t.displayId}</div>
                    <div style={{ fontSize: 11.5, color: T.textSub, marginTop: 3 }}>in {shortDate(t.timeIn)}</div>
                    {t.etaOut && <div style={{ fontSize: 11, marginTop: 3, fontWeight: 600, color: t.isOverdue ? '#e74c3c' : T.textSub }}>
                      promised {shortDate(t.etaOut)}{t.isOverdue ? ' · overdue' : ''}</div>}
                  </div>
                  <AgeBar days={t.daysFinished} T={T} compact />
                  <Due t={t} payment={!t.isPaid ? payments[String(t.id)] : null} theme={theme} T={T} />
                  <div style={{ minWidth: 0 }}>
                    <Reached t={t} contact={contactSummary[t.id]} T={T} />
                    {t.balanceChanged && <div style={{ marginTop: 5 }}><BalanceChanged t={t} theme={theme} onOpen={onOpen} /></div>}
                  </div>
                  <i className="ti ti-chevron-right" style={{ color: T.textSub, fontSize: 16 }} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Card view ────────────────────────────────────────────────────────────────

export function PickupCards({ groups, T, theme, selectedId, onOpen, onCustomer, anchorRefs, contactSummary, payments, lastText, bikesPer }) {
  const isDark = theme === 'dark';
  return (
    <div>
      {groups.map(g => (
        <div key={g.key}>
          <GroupHeader g={g} T={T} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14 }}>
            {g.items.map(t => {
              const tier = tierOf(t.daysFinished);
              const active = selectedId === t.id;
              const msg = lastText[String(t.id)];
              return (
                <div key={t.id} role="button" tabIndex={0} onClick={() => onOpen(t)}
                  onKeyDown={e => { if (e.key === 'Enter') onOpen(t); }}
                  style={{ background: T.card, border: `1px solid ${active ? '#3498db' : T.cardBorder}`, borderTop: `4px solid ${tier.color}`,
                    borderRadius: 12, padding: '14px 16px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 12,
                    transition: 'box-shadow 0.15s, transform 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = isDark ? '0 6px 18px rgba(0,0,0,0.4)' : '0 6px 18px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={t.customer} days={t.daysFinished} isDark={isDark} size={42} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <CustomerName t={t} T={T} theme={theme} onCustomer={onCustomer} anchorRefs={anchorRefs} size={16} />
                      </div>
                      <div style={{ fontSize: 12.5, color: T.textSub, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <i className="ti ti-bike" style={{ marginRight: 5 }} />{t.bike}
                      </div>
                    </div>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: T.textSub, alignSelf: 'flex-start' }}>{t.displayId}</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
                    <Due t={t} payment={!t.isPaid ? payments[String(t.id)] : null} theme={theme} T={T} big />
                    <div>
                      <AgeBar days={t.daysFinished} T={T} compact />
                      <div style={{ fontSize: 11, color: T.textSub, marginTop: 3 }}>in {shortDate(t.timeIn)}{t.etaOut ? ` · promised ${shortDate(t.etaOut)}` : ''}</div>
                    </div>
                  </div>

                  {(msg || bikesPer[t.customerId] > 1 || t.balanceChanged) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {msg && <TextNudge msg={msg} T={T} />}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {bikesPer[t.customerId] > 1 && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#3498db', background: isDark ? '#0f1f2e' : '#e8f2fb', borderRadius: 4, padding: '2px 7px' }}>{bikesPer[t.customerId]} bikes waiting</span>}
                        <BalanceChanged t={t} theme={theme} onOpen={onOpen} />
                      </div>
                    </div>
                  )}

                  <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
                    <Reached t={t} contact={contactSummary[t.id]} T={T} />
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#3498db', whiteSpace: 'nowrap' }}>Open <i className="ti ti-chevron-right" /></span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Texts from pickup customers ──────────────────────────────────────────────

export function PickupTexts({ messages, ticketsById, T, theme, onOpen, layout = 'rail', error = '' }) {
  const isDark = theme === 'dark';
  const unread = messages.filter(m => !m.read).length;
  const strip = layout === 'strip';
  return (
    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: '14px 14px 10px',
      ...(strip ? { marginBottom: 16 } : { position: 'sticky', top: 12 }) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <i className="ti ti-messages" style={{ color: '#3498db', fontSize: 18 }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Texts from pickup customers</span>
        {unread > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#3498db', borderRadius: 10, padding: '1px 7px' }}>{unread} new</span>}
      </div>
      <div style={{ fontSize: 11.5, color: T.textSub, marginBottom: 10 }}>Last 14 days, from customers with a bike on this screen</div>
      {error ? <div role="alert" style={{ fontSize: 12.5, color: '#e74c3c', padding: '8px 0' }}>{error}</div>
        : messages.length === 0 ? <div style={{ fontSize: 12.5, color: T.textSub, fontStyle: 'italic', padding: '8px 0 6px' }}>No texts from these customers lately.</div>
        : (
          <div style={strip ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 } : { display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(strip ? messages.slice(0, 6) : messages).map(m => {
              const t = ticketsById[m.ticketId];
              if (!t) return null;
              return (
                <div key={m.id} role="button" tabIndex={0} onClick={() => onOpen(t)} onKeyDown={e => { if (e.key === 'Enter') onOpen(t); }}
                  title="Open this bike's pickup, on the Text tab"
                  style={{ display: 'flex', gap: 10, padding: '9px 10px', borderRadius: 9, cursor: 'pointer',
                    background: !m.read ? (isDark ? '#132338' : '#eef5fc') : 'transparent', border: `1px solid ${!m.read ? (isDark ? '#1f3a5c' : '#d4e6f7') : T.border}` }}>
                  <Avatar name={t.customer} days={t.daysFinished} isDark={isDark} size={32} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      {!m.read && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#3498db', flexShrink: 0, alignSelf: 'center' }} />}
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.customer}</span>
                      <span style={{ fontSize: 11, color: T.textSub, marginLeft: 'auto', flexShrink: 0 }}>{ago(m.createdAt)}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: T.text, marginTop: 3, lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {m.body || (m.media ? <i>Sent a photo</i> : '')}
                    </div>
                    <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>
                      {t.bike} · {t.isPaid ? <span style={{ color: '#27ae60', fontWeight: 600 }}>Paid</span> : <span style={{ fontWeight: 600, color: T.text }}>{money(t.owed)} due</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
